import { useRef, useState } from "react";
import { Paperclip, Trash2, Upload } from "lucide-react";

import "./GeneralApplications.css";
import { createCareer } from "../../../api/CareerApi";

const GeneralApplication = () => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    link: "",
    consent: false,
  });

  const [resume, setResume] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setError("");
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a PDF or Word document.");
      e.target.value = "";
      return;
    }

    if (file.size > maxSize) {
      setError("Resume size must be less than 5 MB.");
      e.target.value = "";
      return;
    }

    setError("");

    setResume({
      file,
      name: file.name,
      progress: 100,
    });
  };

  const handleRemoveResume = () => {
    setResume(null);
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!formData.consent) {
      setError(
        "Please agree to receive updates and marketing communications from Xyncely."
      );
      return;
    }

    if (!resume) {
      setError("Please upload your resume.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = new FormData();

      data.append("name", formData.name.trim());
      data.append("email", formData.email.trim());
      data.append("phone_number", formData.phone.trim());

      data.append(
        "linkedin_portfolio_link",
        formData.link.trim()
      );

      data.append("resume", resume.file);

      await createCareer(data);

      setSuccess(true);

      setFormData({
        name: "",
        email: "",
        phone: "",
        link: "",
        consent: false,
      });

      setResume(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error(
        "General application error:",
        err
      );

      const detail = err?.response?.data?.detail;

      if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item.msg)
            .join(", ")
        );
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(
          "Something went wrong. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="general-application">
      {/* Header */}
      <div className="general-application__header">
        <span className="general-application__eyebrow">
          GENERAL APPLICATION
        </span>

        <h2>Don't see your role?</h2>

        <p>
          We are always looking for passionate builders,
          designers, and strategists. Apply below and
          we'll keep you in mind for future openings.
        </p>
      </div>

      {/* Success */}
      {success ? (
        <div className="general-application__card">
          <div className="general-application__success">
            <h3>Application Submitted!</h3>

            <p>
              Thank you for applying to Xyncely.
              We’ll get back to you soon.
            </p>

            <button
              type="button"
              className="general-application__submit"
              onClick={() => setSuccess(false)}
            >
              Submit Another Application
            </button>
          </div>
        </div>
      ) : (
        /* Form Card */
        <div className="general-application__card">
          <form
            className="general-application__form"
            onSubmit={handleSubmit}
          >
            {/* Full Name */}
            <div className="general-application__field">
              <label htmlFor="application-name">
                Full Name <span>*</span>
              </label>

              <input
                id="application-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Jane Doe"
                required
              />
            </div>

            {/* Email */}
            <div className="general-application__field">
              <label htmlFor="application-email">
                Email Address <span>*</span>
              </label>

              <input
                id="application-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jane@example.com"
                required
              />
            </div>

            {/* Mobile Number */}
            <div className="general-application__field">
              <label htmlFor="application-phone">
                Mobile Number <span>*</span>
              </label>

              <input
                id="application-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                inputMode="tel"
                required
              />
            </div>

            {/* LinkedIn */}
            <div className="general-application__field">
              <label htmlFor="application-link">
                LinkedIn or Portfolio Link <span>*</span>
              </label>

              <input
                id="application-link"
                name="link"
                type="url"
                value={formData.link}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/username"
                required
              />
            </div>

            {/* Resume */}
            <div className="general-application__resume">
              <div className="general-application__resume-header">
                <label htmlFor="resume-upload">
                  Upload Resume (PDF or Word){" "}
                  <span>*</span>
                </label>

                {resume && (
                  <span>
                    Uploaded {resume.progress}%
                  </span>
                )}
              </div>

              {!resume ? (
                <>
                  <input
                    ref={fileInputRef}
                    id="resume-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleResumeChange}
                    className="general-application__file-input"
                  />

                  <label
                    htmlFor="resume-upload"
                    className="general-application__upload"
                  >
                    <Upload size={18} />

                    <span>
                      Upload your resume
                    </span>

                    <small>
                      PDF or Word · Max 5 MB
                    </small>
                  </label>
                </>
              ) : (
                <>
                  <div className="general-application__file">
                    <div className="general-application__file-left">
                      <Paperclip size={15} />

                      <span>
                        {resume.name}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="general-application__delete"
                      onClick={handleRemoveResume}
                      aria-label="Remove resume"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="general-application__progress">
                    <div
                      className="general-application__progress-bar"
                      style={{
                        width: `${resume.progress}%`,
                      }}
                    />
                  </div>
                </>
              )}

              {error && (
                <p className="general-application__error">
                  {error}
                </p>
              )}
            </div>

            {/* CONSENT */}
            <label className="general-application__consent">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
              />

              <span>
                I agree to receive updates and marketing
                communications from <strong>Xyncely</strong>
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              className="general-application__submit"
              disabled={loading}
            >
              {loading
                ? "Submitting..."
                : "Submit Application"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
};

export default GeneralApplication;